import { useState, useEffect } from 'react';
import { dgiiApi } from '../../api/dgii';
import {
	ClockIcon,
	CheckCircleIcon,
	XCircleIcon,
	ArrowPathIcon,
	Bars3Icon,
} from '@heroicons/react/24/outline';

// Icono de cola (usar Bars3Icon como alternativa)
const QueueIcon = Bars3Icon;

export default function ColaProcesamiento({ archivoId = null, onUpdate = null, timestampInicio = null }) {
	const [estadoCola, setEstadoCola] = useState(null);
	const [estadoTarea, setEstadoTarea] = useState(null);
	const [loading, setLoading] = useState(false);
	const [error, setError] = useState(null);

	// Polling cada 3 segundos si hay tareas activas
	useEffect(() => {
		const cargarEstado = async () => {
			try {
				setLoading(true);
				setError(null);

			// Si se proporciona archivoId, cargar estado de esa tarea específica
			if (archivoId) {
				try {
					const tarea = await dgiiApi.getEstadoTarea(archivoId);
					// found=false → el archivo ya no existe, limpiar estado
					setEstadoTarea(tarea?.found === false ? null : tarea);
				} catch (err) {
					// Silenciar 404 (archivo eliminado/reseteado)
					if (err.response?.status !== 404) {
						console.error('Error cargando estado de tarea:', err);
					}
					setEstadoTarea(null);
				}
			}

				// Cargar estado general de la cola
				// Si hay timestamp de inicio, pasarlo para filtrar solo esa sesión
				const cola = await dgiiApi.getEstadoCola(timestampInicio);
				setEstadoCola(cola);
			} catch (err) {
				console.error('Error cargando estado de cola:', err);
				setError('Error al cargar estado de la cola');
			} finally {
				setLoading(false);
			}
		};

		cargarEstado();

		// Polling cada 5s; sin depender de estadoCola para no re-ejecutar el efecto en cada actualización
		const interval = setInterval(cargarEstado, 5000);

		return () => clearInterval(interval);
	}, [archivoId, timestampInicio]);

	// Notificar cuando se complete una tarea
	useEffect(() => {
		if (estadoTarea && estadoTarea.estado === 'completada' && onUpdate) {
			onUpdate(estadoTarea);
		}
	}, [estadoTarea, onUpdate]);

	if (loading && !estadoCola && !estadoTarea) {
		return (
			<div className="flex items-center space-x-2 text-sm text-gray-600">
				<ClockIcon className="h-4 w-4 animate-spin" />
				<span>Cargando estado...</span>
			</div>
		);
	}

	// Si se está monitoreando un archivo específico
	if (archivoId) {
		if (!estadoTarea) {
			return null; // No hay tarea para este archivo aún
		}

		const getEstadoColor = (estado) => {
			switch (estado) {
				case 'en_cola':
					return 'text-yellow-600 bg-yellow-50';
				case 'procesando':
					return 'text-blue-600 bg-blue-50';
				case 'completada':
					return 'text-green-600 bg-green-50';
				case 'error':
					return 'text-red-600 bg-red-50';
				case 'cancelada':
					return 'text-gray-600 bg-gray-50';
				default:
					return 'text-gray-600 bg-gray-50';
			}
		};

		const getEstadoIcon = (estado) => {
			switch (estado) {
				case 'en_cola':
					return <QueueIcon className="h-4 w-4" />;
				case 'procesando':
					return <ClockIcon className="h-4 w-4 animate-spin" />;
				case 'completada':
					return <CheckCircleIcon className="h-4 w-4" />;
				case 'error':
					return <XCircleIcon className="h-4 w-4" />;
				default:
					return <ClockIcon className="h-4 w-4" />;
			}
		};

		return (
			<div className={`inline-flex items-center space-x-2 px-3 py-1.5 rounded-lg text-xs font-medium ${getEstadoColor(estadoTarea.estado)}`}>
				{getEstadoIcon(estadoTarea.estado)}
				<span>{estadoTarea.estado_display}</span>
				{estadoTarea.progreso > 0 && estadoTarea.progreso < 100 && (
					<span className="ml-2">({estadoTarea.progreso}%)</span>
				)}
				{estadoTarea.estado === 'procesando' && (
					<div className="ml-2 w-16 bg-gray-200 rounded-full h-1.5">
						<div
							className="bg-blue-600 h-1.5 rounded-full transition-all duration-300"
							style={{ width: `${estadoTarea.progreso}%` }}
						/>
					</div>
				)}
			</div>
		);
	}

	// Vista general de la cola
	// Si no hay timestamp de inicio, no mostrar nada (no histórico)
	if (!timestampInicio) {
		return null;
	}

	if (!estadoCola) {
		return null;
	}

	const tieneTareasActivas = estadoCola.total_en_cola > 0 || estadoCola.total_procesando > 0 || estadoCola.total_completadas > 0 || estadoCola.total_errores > 0;

	if (!tieneTareasActivas && !error) {
		return null; // No mostrar nada si no hay tareas de la carga actual
	}

	return (
		<div className="bg-white rounded-lg shadow-md p-4 border border-gray-200">
			<div className="flex items-center justify-between mb-3">
				<div className="flex items-center space-x-2">
					<QueueIcon className="h-5 w-5 text-primary" />
					<h4 className="text-sm font-semibold text-gray-800">Estado de Cola de Procesamiento</h4>
				</div>
				<button
					onClick={() => {
						setEstadoCola(null);
						setEstadoTarea(null);
					}}
					className="text-gray-400 hover:text-gray-600"
					title="Actualizar"
				>
					<ArrowPathIcon className="h-4 w-4" />
				</button>
			</div>

			{error && (
				<div className="mb-3 p-2 bg-red-50 border border-red-200 rounded text-xs text-red-600">
					{error}
				</div>
			)}

			{/* Estadísticas */}
			<div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-3">
				<div className="bg-yellow-50 border border-yellow-200 rounded p-2">
					<div className="text-xs text-yellow-600 font-medium">En Cola</div>
					<div className="text-lg font-bold text-yellow-700">{estadoCola.total_en_cola || 0}</div>
				</div>
				<div className="bg-blue-50 border border-blue-200 rounded p-2">
					<div className="text-xs text-blue-600 font-medium">Procesando</div>
					<div className="text-lg font-bold text-blue-700">{estadoCola.total_procesando || 0}</div>
				</div>
				<div className="bg-green-50 border border-green-200 rounded p-2">
					<div className="text-xs text-green-600 font-medium">Completadas</div>
					<div className="text-lg font-bold text-green-700">{estadoCola.total_completadas || 0}</div>
				</div>
				<div className="bg-red-50 border border-red-200 rounded p-2">
					<div className="text-xs text-red-600 font-medium">Errores</div>
					<div className="text-lg font-bold text-red-700">{estadoCola.total_errores || 0}</div>
				</div>
			</div>

			{/* Lista de tareas activas */}
			{estadoCola.tareas_activas && estadoCola.tareas_activas.length > 0 && (
				<div className="mt-3 space-y-2">
					<div className="text-xs font-medium text-gray-700 mb-2">Tareas Activas:</div>
					{estadoCola.tareas_activas.map((tarea) => {
						const getEstadoColor = (estado) => {
							switch (estado) {
								case 'en_cola':
									return 'border-yellow-200 bg-yellow-50';
								case 'procesando':
									return 'border-blue-200 bg-blue-50';
								default:
									return 'border-gray-200 bg-gray-50';
							}
						};

						return (
							<div
								key={tarea.id}
								className={`p-2 rounded border ${getEstadoColor(tarea.estado)}`}
							>
								<div className="flex items-center justify-between">
									<div className="flex-1 min-w-0">
										<div className="text-xs font-medium text-gray-800 truncate">
											{tarea.archivo_nombre || 'Sin nombre'}
										</div>
										<div className="text-xs text-gray-600 mt-1">
											{tarea.estado_display}
											{tarea.progreso > 0 && ` - ${tarea.progreso}%`}
										</div>
									</div>
									{tarea.estado === 'procesando' && tarea.progreso > 0 && (
										<div className="ml-3 w-20 bg-gray-200 rounded-full h-1.5">
											<div
												className="bg-blue-600 h-1.5 rounded-full transition-all duration-300"
												style={{ width: `${tarea.progreso}%` }}
											/>
										</div>
									)}
								</div>
								{tarea.error && (
									<div className="mt-1 text-xs text-red-600 truncate" title={tarea.error}>
										Error: {tarea.error}
									</div>
								)}
							</div>
						);
					})}
				</div>
			)}
		</div>
	);
}
